"use client";
import { useTranslations } from "next-intl"

import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle, ChevronDown, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { motion } from "framer-motion";

interface ContactFormProps {
  defaults: {
    first_name: string;
    last_name: string;
    email: string;
    username: string;
  };
}

type FormDataState = {
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  reason: string;
  message: string;
  target_url: string;
  other_reason: string;
  captcha: string;
};

type ContactPayload = {
  first_name: string;
  last_name: string;
  username: string | null;
  email: string;
  reason: string;
  message: string;
  target_url: string | null;
  other_reason: string | null;
};

const generateMathCaptcha = () => {
  const a = Math.floor(Math.random() * 10);
  const b = Math.floor(Math.random() * 10);
  return { question: `${a} + ${b} = ?`, answer: (a + b).toString() };
};

function toStr(v: unknown): string {
  return v != null && typeof v === "string" ? v : "";
}

export default function ContactForm({ defaults }: ContactFormProps) {
  const tContact = useTranslations("contact");
  const safeDefaults = useMemo(
    () => ({
      first_name: toStr(defaults?.first_name),
      last_name:  toStr(defaults?.last_name),
      email:      toStr(defaults?.email),
      username:   toStr(defaults?.username),
    }),
    [defaults]
  );
  const [formData, setFormData] = useState<FormDataState>(() => ({
    first_name:   safeDefaults.first_name,
    last_name:    safeDefaults.last_name,
    email:        safeDefaults.email,
    username:     safeDefaults.username,
    reason:       "",
    message:      "",
    target_url:   "",
    other_reason: "",
    captcha:      "",
  }));
  const [captcha, setCaptcha] = useState({ question: "", answer: "" });
  const [mounted, setMounted] = useState(false);

  const [captchaError, setCaptchaError] = useState("");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setCaptcha(generateMathCaptcha());
  }, []);

  const refreshCaptcha = () => {
    setCaptcha(generateMathCaptcha());
    setFormData((p) => ({ ...p, captcha: "" }));
    setCaptchaError("");
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (name === "captcha") setCaptchaError("");
    setFormError("");
  };

  const handleReasonChange = (reason: string) => {
    setFormData((p) => ({
      ...p,
      reason,
      target_url:   "",
      other_reason: "",
    }));
    setFormError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (formData.captcha !== captcha.answer) {
      setCaptchaError(tContact("incorrectAnswer"));
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: ContactPayload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        username: formData.username || null,
        reason: formData.reason,
        message: formData.message,
        target_url: formData.target_url || null,
        other_reason: formData.other_reason || null,
      };

      const res = await fetch("/api/contact", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      let body: { success?: boolean; error?: string } = {};
      try {
        const text = await res.text();
        if (text) body = JSON.parse(text) as { success?: boolean; error?: string };
      } catch {
        throw new Error(res.ok ? "Invalid response from server" : "Request failed");
      }
      if (!res.ok) throw new Error(body.error || tContact("failed"));

      setIsSubmitted(true);
    } catch (err: any) {
      console.error(err);
      setFormError(err.message || "An unexpected error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const reasonOptions = useMemo(
    () =>
      [
        { value: "report_listing", label: "Report a listing" },
        { value: "report_user", label: "Report a user" },
        { value: "general_inquiry", label: "General inquiry" },
        { value: "technical_support", label: "Technical support" },
        { value: "feedback", label: "Feedback" },
        { value: "partnership", label: "Partnership opportunity" },
        { value: "service_fee_refund", label: "Request a service fee refund" },
        { value: "other", label: "Other" },
      ] as const,
    [],
  );

  const reasonLabel = useMemo(() => {
    if (!formData.reason) return tContact("selectReason");
    return reasonOptions.find(option => option.value === formData.reason)?.label ?? tContact("selectReason");
  }, [formData.reason, reasonOptions]);

  const handleSubmitAnother = () => {
    setIsSubmitted(false);
    setFormData({
      first_name:   safeDefaults.first_name,
      last_name:    safeDefaults.last_name,
      email:        safeDefaults.email,
      username:     safeDefaults.username,
      reason:       "",
      message:      "",
      target_url:   "",
      other_reason: "",
      captcha:      "",
    });
    refreshCaptcha();
  };

  if (isSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[200px] text-center px-4">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
          className="text-emerald-500 mb-4"
        >
          <CheckCircle className="w-16 h-16 mx-auto" strokeWidth={1.5} />
        </motion.div>
        <motion.h3
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xl font-semibold text-emerald-500"
        >
          {tContact("messageSent")}
        </motion.h3>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-muted-foreground mt-1"
        >
          Thanks for reaching out. We’ll be in touch soon.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-6"
        >
          <Button type="button" variant="outline" onClick={handleSubmitAnother} className="rounded-full">
            Submit another request
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 w-full">
      {formError ? (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {formError}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="first_name">{tContact("firstName")}</Label>
          <Input
            id="first_name"
            name="first_name"
            value={formData.first_name ?? ""}
            onChange={handleChange}
            required
            disabled={Boolean(safeDefaults.first_name)}
            className="rounded-full text-base"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name">{tContact("lastName")}</Label>
          <Input
            id="last_name"
            name="last_name"
            value={formData.last_name ?? ""}
            onChange={handleChange}
            required
            disabled={Boolean(safeDefaults.last_name)}
            className="rounded-full text-base"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">{tContact("email")}</Label>
        <Input
          id="email"
          name="email"
          type="email"
            value={formData.email ?? ""}
            onChange={handleChange}
            required
            disabled={Boolean(safeDefaults.email)}
          className="rounded-full text-base"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="username">{tContact("username")}</Label>
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            @
          </span>
          <Input
            id="username"
            name="username"
            value={formData.username ?? ""}
            onChange={handleChange}
            placeholder={tContact("optional")}
            disabled={Boolean(safeDefaults.username)}
            className="pl-8 rounded-full text-base"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="reason">{tContact("reason")}</Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="h-12 w-full justify-between rounded-full text-base font-normal"
            >
              <span className="truncate">{reasonLabel}</span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="w-(--radix-popper-anchor-width) p-1"
          >
            {reasonOptions.map(option => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => handleReasonChange(option.value)}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {["report_listing", "report_user"].includes(formData.reason) ? (
        <div className="space-y-2">
          <Label htmlFor="target_url">
            URL of {formData.reason === "report_listing" ? "listing" : "user"}
          </Label>
          <Input
            id="target_url"
            name="target_url"
            value={formData.target_url ?? ""}
            onChange={handleChange}
            placeholder="https://kraahl.com/…"
            required
            className="text-base"
          />
        </div>
      ) : null}

      {formData.reason === "other" ? (
        <div className="space-y-2">
          <Label htmlFor="other_reason">{tContact("optional")}</Label>
          <Input
            id="other_reason"
            name="other_reason"
            value={formData.other_reason ?? ""}
            onChange={handleChange}
            required
            className="rounded-full text-base"
          />
        </div>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="message">{tContact("message")}</Label>
        <Textarea
          id="message"
          name="message"
            value={formData.message ?? ""}
          onChange={handleChange}
          required
          className="min-h-32 text-base"
        />
      </div>

      {mounted ? (
        <div className="space-y-3 rounded-2xl border border-border bg-muted/40 p-4">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="captcha">Verify you&apos;re human</Label>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={refreshCaptcha}
              aria-label="Refresh captcha"
              className="h-9 w-9 rounded-full"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="font-mono text-base text-foreground">{captcha.question}</div>
            <Input
              id="captcha"
              name="captcha"
              value={formData.captcha ?? ""}
              onChange={handleChange}
              required
              className="rounded-full text-base sm:max-w-[240px]"
              aria-invalid={Boolean(captchaError)}
            />
          </div>
          {captchaError ? <p className="text-sm text-destructive">{captchaError}</p> : null}
        </div>
      ) : null}

      <Button
        type="submit"
        disabled={isSubmitting || !formData.reason || !formData.message.trim()}
        className="w-full rounded-full text-base"
      >
        {isSubmitting ? tContact("sending") : tContact("send")}
      </Button>
    </form>
  );
}
